import { AppointmentListingModel, HospitalModel, HospitalScheduleModel, schedule } from "@common/models";
import { AppointmentStatus, EntityScheduleType, WeekDay } from "@common/models/Enums";
import { HospitalScheduleDateRangeType } from "@common/models/Enums/HospitalScheduleDateRangeType";
import { HospitalScheduleRequestModel, EntityScheduleOverridePreferenceRequest } from "@common/models/requests";
import { HospitalService } from "@common/services";
import { NotificationStoreInstance } from "@common/stores/NotificationStore";
import { allDaysBetween, DateTimeUtils, fallsOnDayOfWeek } from "@common/utils/datetimeUtils";
import { hideSameDayReservationFromSameDay } from "@hospital/utils";
import { OverrideForm } from "@models/forms/OverrideForm";
import { DateTime } from "luxon";
import { computed, flow, IObservableArray, makeObservable, observable, runInAction } from "mobx";

export class HospitalScheduleStore {
    @observable _currentHospital: HospitalModel;
    @observable _schedule: schedule.ProcessedHospitalScheduleModel = schedule.ProcessedHospitalScheduleModel.getDefault();
    @observable _appointments: IObservableArray<AppointmentListingModel> = observable.array<AppointmentListingModel>([]);
    @observable _loadedDate?: DateTime;
    @observable _dateRange?: HospitalScheduleDateRangeType;

    constructor(currentHospital: HospitalModel) {
        this._currentHospital = currentHospital;
        makeObservable(this, undefined, { autoBind: true });
    }

    @computed get hospitalId() {
        return this._currentHospital.hospitalId;
    }

    @computed get schedule() {
        return this._schedule;
    }

    @computed get scheduleDay() {
        return _.first(_.first(this._schedule.hospitalSchedules)!.schedule)!;
    }

    @computed get scheduledEntities() {
        return this.scheduleDay.scheduledEntities;
    }

    @computed get allVets() {
        return this._schedule.veterinarians;
    }

    @computed get licensedVetsWithAccess() {
        return this._schedule.getVeterinarians(this._currentHospital.hospitalId, { day: this.scheduleDay.day, hasLicense: true });
    }

    @computed get unscheduledLicensedVetsWithAccess() {
        return this._schedule.getVeterinarians(this._currentHospital.hospitalId, { day: this.scheduleDay.day, isScheduled: false, hasLicense: true, hasAccess: true });
    }

    @computed get allTemplates() {
        return this._schedule.templates;
    }

    @computed get activeTemplates() {
        return this._schedule.getTemplates(this._currentHospital.hospitalId, { day: this.scheduleDay.day, isActive: true });
    }

    @computed get activeScheduleTemplates() {
        return this._schedule.getTemplates(this._currentHospital.hospitalId, { day: this.scheduleDay.day, isActive: true, isScheduled: true });
    }

    @computed get scheduledVeterinarians() {
        return this._schedule.veterinarians.filter(v => _.some(this.scheduleDay.scheduledEntities.filter(e => e.entityType === EntityScheduleType.Veterinarian && e.entityId === v.veterinarianId)));

    }
    @computed get allScheduledTemplates() {
        return this._schedule.getTemplates(this._currentHospital.hospitalId, { day: this.scheduleDay.day, isScheduled: true });
    }

    @computed get appointments() {
        return this._appointments;
    }


    /**
     * Filters all appointments that require no further action
     */
    @computed get completedAppointments(): Array<AppointmentListingModel> {
        return _.filter(this._appointments, (appointment) => {
            return this.isCompletedAppointment(appointment);
        });
    }

    /**
     * Filters all applicable appointments today, hides completed appointments
     */
    @computed get todayAppointments(): Array<AppointmentListingModel> {
        return _.reject(this._appointments, (appointment) => {
            return this.isCompletedAppointment(appointment);
        });
    }

    load = flow(function* (this: HospitalScheduleStore, date: DateTime, range: HospitalScheduleDateRangeType) {
        yield Promise.all([
            HospitalService.getHospitalSchedules(this._currentHospital.hospitalId, date, range),
            HospitalService.getAppointments(this._currentHospital.hospitalId, date, date.endOf('day'))
        ]).then(([schedule, appointments]) => {
            runInAction(() => {
                this._loadedDate = date;
                this._dateRange = range;
                this._schedule = schedule;
                this._appointments.clear();
                this._appointments = observable(appointments.filter(appt => hideSameDayReservationFromSameDay(appt, appointments)));
            })
        });
    });

    refresh = flow(function* (this: HospitalScheduleStore) {
        if (this._loadedDate && this._dateRange)
            yield this.load(this._loadedDate, this._dateRange);
        else
            throw 'refresh was called on HospitalScheduleStore in an invalid state, loadedDate or dateRange was never loaded.  The schedule store should be loaded before using refresh.';
    });

    addAppointment(value: AppointmentListingModel) {
        this._appointments.push(value);
    }

    /**
     * Determines if the current appointment list is stale and should be refetched
     */
    checkForAppointmentUpdates = flow(function* (this: HospitalScheduleStore, date: DateTime) {
        const lastModifiedAppointmentTime = yield HospitalService.getLastModifiedAppointmentDate(
            this._currentHospital!.hospitalId, date, date.endOf('day'))
            .catch(NotificationStoreInstance.onError("Failed to sync Appointments", undefined));

        if (lastModifiedAppointmentTime == null) return false;

        // Find the last modified appointment
        const sortedDates = this._appointments.map(x => DateTime.fromISO(x.lastModifiedDate, { zone: "UTC" }))
            .sort((a: DateTime, b: DateTime) => DateTimeUtils.sortDescDateTimes(a, b))

        const lastModified = _.first(sortedDates);

        // Check if an appointment for the current day/hospital has been updated since our last load
        return (lastModified === undefined || lastModified < DateTime.fromISO(lastModifiedAppointmentTime, { zone: "UTC" }));
    })

    addAdhocHospitalSchedule = flow(function* (this: HospitalScheduleStore, hospitalId: number, schedule: HospitalScheduleRequestModel) {
        yield HospitalService.addAdhocHospitalSchedule(hospitalId, schedule).catch(NotificationStoreInstance.onError('The Doctor failed to be added to the schedule.', undefined));
    });

    updateHospitalSchedule = flow(function* (this: HospitalScheduleStore, hospitalId: number, date: DateTime, schedule: HospitalScheduleRequestModel[]): Generator<Promise<boolean>, HospitalScheduleModel[], any> {
        return yield HospitalService.updateHospitalSchedules(hospitalId, date, schedule, { showSuccess: true });
    });

    private isCompletedAppointment(appointment: AppointmentListingModel): boolean {
        return _.includes([AppointmentStatus.Cancelled, AppointmentStatus.Rescheduled, AppointmentStatus.NoShow, AppointmentStatus.Check_Out], appointment.appointmentStatusId);
    }

    public updateSchedulePreferenceOverrides = flow(function* (this: HospitalScheduleStore, overrides: EntityScheduleOverridePreferenceRequest[], date: DateTime) {
        return yield HospitalService.updateHospitalSchedulePreferenceOverrides(this._currentHospital.hospitalId, date, overrides)
            .then(() => NotificationStoreInstance.success("Overrides Updated!"))
            .catch((error) => NotificationStoreInstance.error(`Failed to update overrides. ${error.message}`));
    });

    public createSchedulePreferenceOverrides = flow(function* (this: HospitalScheduleStore, data: OverrideForm, startDate: string) {
        const startTime = data.startTime && DateTime.fromJSDate(data.startTime!).set({ millisecond: 0 }).toISOTime({ includeOffset: false, suppressMilliseconds: true });
        const endTime = data.endTime && DateTime.fromJSDate(data.endTime!).set({ millisecond: 0 }).toISOTime({ includeOffset: false, suppressMilliseconds: true });
        const selectedDays = data.repeatDays.filter(e => e.selected).map(a => a.day);
        const dataVets = data.vets.filter(e => e.selected);
        const dataTemplates = data.templates.filter(e => e.selected);
        const dataStaticEntities = data.staticEntities.filter(e => e.selected);
        const recurrenceDates = this.getRecurrenceDates(startDate, data.repeatUntil!, selectedDays).filter(rd => rd !== DateTime.fromISO(startDate, { zone: 'UTC' }).toFormat('yyyy-LL-dd'));

        //Create override requests for every selected schedule entity on the override form
        const initialRequests = [...dataVets, ...dataTemplates, ...dataStaticEntities].map((entity) => {
            return new EntityScheduleOverridePreferenceRequest({
                storeId: this.hospitalId,
                entityType: entity.entityType,
                entityId: entity.entityId as EntityScheduleType,
                overrideDate: DateTime.fromISO(startDate, { zone: 'UTC' }).toFormat('yyyy-LL-dd'),
                startTime: startTime,
                endTime: endTime,
                dayOfWeek: WeekDay.fromDateTime(DateTime.fromISO(startDate, { zone: 'UTC' })),
                scheduleBlockTypeId: data.scheduleBlockTypeId,
                notes: data.notes
            });
        });


        //For each selected recurring date, copy all the initialRequests created from selected entities but with the recurring overrideDate/dayOfWeek updated
        const recurringRequests = _.flatMap(initialRequests.map(req => {
            return recurrenceDates.map(overrideDate => {
                return { ...req, ...{ overrideDate }, dayOfWeek: WeekDay.fromDateTime(DateTime.fromFormat(overrideDate, 'yyyy-LL-dd', { zone: 'UTC' })) }
            });
        }));

        return yield HospitalService.createHospitalSchedulePreferenceOverrides(this._currentHospital.hospitalId, startDate, _.concat(initialRequests, recurringRequests))
            .then(() => NotificationStoreInstance.success("Override Created!"))
            .catch((error) => NotificationStoreInstance.error(`Failed to create override. ${error.message}`));
    });

    /**
    *  Get all the days that match the current recurrence parameters
    * @param startDate 
    * @param endDate 
    * @param daysOfTheWeek 
    */
    getRecurrenceDates = (startDate: string, endDate: string, daysOfTheWeek: WeekDay.Day[]) => {
        const requestedDates = Array.from(allDaysBetween(DateTime.fromISO(startDate, { zone: 'UTC' }), DateTime.fromISO(endDate, { zone: 'UTC' })));
        return requestedDates.filter(rd => daysOfTheWeek.some(ad => fallsOnDayOfWeek(ad, rd))).map(d => d.toISODate());
    }

}
